import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../ui/Button";

interface HeaderProps {
  title?: string;
  showBackButton?: boolean;
}

const Header: React.FC<HeaderProps> = ({ title = "Vehicle Billing System", showBackButton = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Parse stored user object
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
    setIsMenuOpen(false);
  };

  const menuItems = [
    {
      label: "My Transactions",
      icon: "💳",
      onClick: () => {
        navigate("/invoices");
        setIsMenuOpen(false);
      }
    },
    {
      label: "Consolidated Reports",
      icon: "📊",
      onClick: () => {
        navigate("/reports");
        setIsMenuOpen(false);
      }
    },
    {
      label: "User Reports",
      icon: "👤",
      onClick: () => {
        // Navigate to user reports page (you can create this later)
        console.log("Navigate to user reports");
        setIsMenuOpen(false);
      }
    }
  ];

  const handleMenuItemClick = (item: any) => {
    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Back button and title */}
          <div className="flex items-center space-x-4">
            {showBackButton && (
              <Button
                color="secondary"
                onClick={() => navigate(-1)}
                className="flex items-center space-x-1"
              >
                <span>←</span>
                <span className="hidden sm:inline">Back</span>
              </Button>
            )}
            <div className="flex items-center space-x-3">
              <div className="text-2xl">🚗</div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                {title}
              </h1>
            </div>
          </div>

          {/* Right side - User menu */}
          <div className="flex items-center space-x-4">
            {user && (
              <div className="hidden sm:block text-sm text-gray-600">
                Welcome, <span className="font-medium">{user.username}</span>
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                  {user.role}
                </span>
              </div>
            )}
            
            {/* Menu Button */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <span className="text-lg">☰</span>
                <span className="hidden sm:inline font-medium">Menu</span>
                <span className={`transform transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>

              {/* Dropdown Menu */}
              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {/* User info in mobile */}
                  {user && (
                    <div className="px-4 py-3 border-b border-gray-100 sm:hidden">
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Role: <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">{user.role}</span>
                      </div>
                    </div>
                  )}

                  {/* Menu Items */}
                  {menuItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleMenuItemClick(item)}
                      className="flex items-center space-x-3 w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}

                  {/* Logout */}
                  <hr className="border-gray-100 my-2" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 w-full px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <span>🚪</span>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;